import { forwardRef, useImperativeHandle, useRef } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'

// import Loader from '@/components/base/Loader'
import Logo from '@/components/base/Logo'
import Typography from '@/components/base/Typography'

import {
  MASK_TRANSITION_ENTER_DURATION,
  MASK_TRANSITION_LEAVE_DURATION,
  MASK_TRANSITION_STAGGER_AMOUNT
} from '@/contexts/transition'

export type MaskTransitionHandler = {
  enterTimeline: () => gsap.core.Timeline
  leaveTimeline: () => gsap.core.Timeline
}

const MaskTransition = forwardRef<MaskTransitionHandler>((_, ref) => {
  const root = useRef<HTMLDivElement>(null)

  const { contextSafe } = useGSAP({ scope: root })

  const enterTimeline = contextSafe(() => {
    return gsap
      .timeline()
      .set(root.current, { display: 'block' })
      .add(
        gsap.timeline().fromTo(
          ['[data-mask-green]', '[data-mask-cyan]', '[data-mask-white]'],
          { yPercent: 100 },
          {
            yPercent: 0,
            duration: MASK_TRANSITION_ENTER_DURATION,
            stagger: MASK_TRANSITION_STAGGER_AMOUNT,
            ease: 'power3.in',
            force3D: true
          }
        )
      )
      .add(
        gsap
          .timeline()
          .fromTo(
            '[data-mask-transparent]',
            { yPercent: 100 },
            {
              yPercent: 0,
              duration: MASK_TRANSITION_ENTER_DURATION,
              ease: 'power3.in',
              force3D: true
            }
          )
          .fromTo(
            ['[data-mask-logo]', '[data-mask-loader]'],
            { yPercent: -100 },
            {
              yPercent: 0,
              duration: MASK_TRANSITION_ENTER_DURATION,
              ease: 'power3.in',
              force3D: true
            },
            '<'
          ),
        `<+${MASK_TRANSITION_STAGGER_AMOUNT * 2}`
      )
  })

  const leaveTimeline = contextSafe(() => {
    return gsap
      .timeline()
      .add(
        gsap
          .timeline()
          .fromTo(
            '[data-mask-transparent]',
            { yPercent: 0 },
            {
              yPercent: -100,
              duration: MASK_TRANSITION_LEAVE_DURATION,
              ease: 'power3.out',
              force3D: true
            }
          )
          .fromTo(
            ['[data-mask-logo]', '[data-mask-loader]'],
            { yPercent: 0 },
            {
              yPercent: 100,
              duration: MASK_TRANSITION_LEAVE_DURATION,
              ease: 'power3.out',
              force3D: true
            },
            '<'
          ),
        '<'
      )
      .add(
        gsap.timeline().fromTo(
          ['[data-mask-white]', '[data-mask-cyan]', '[data-mask-green]'],
          { yPercent: 0 },
          {
            yPercent: -100,
            duration: MASK_TRANSITION_LEAVE_DURATION,
            stagger: MASK_TRANSITION_STAGGER_AMOUNT,
            ease: 'power3.out',
            force3D: true
          }
        ),
        '<'
      )

      .set(root.current, { display: 'none' })
  })

  useImperativeHandle(
    ref,
    () => ({
      leaveTimeline,
      enterTimeline
    }),
    [enterTimeline, leaveTimeline]
  )

  return (
    <div ref={root} className="fixed left-0 top-0 z-transition h-screen w-full">
      <div
        className="h-dvh-vh absolute left-0 top-0 w-full bg-blue-primary"
        data-mask-green
      />
      <div
        className="h-dvh-vh absolute left-0 top-0 w-full bg-blue-dark"
        data-mask-cyan
      />
      <div
        className="h-dvh-vh absolute left-0 top-0 w-full bg-dark"
        data-mask-white
      />
      <div
        className="h-dvh-vh absolute left-0 top-0 w-full overflow-hidden"
        data-mask-transparent
      >
        <div
          className="h-dvh-vh absolute left-0 top-0 flex w-full items-center justify-center"
          data-mask-logo
        >
          <div className="flex flex-col items-center justify-center gap-5">
            <Logo />
          </div>
        </div>

        <div className="h-dvh-vh absolute left-0 top-0 w-full" data-mask-loader>
          {/* <Loader className="absolute bottom-lg left-1/2 h-[5rem] w-[5rem] -translate-x-1/2 md:bottom-2xl" /> */}
        </div>
      </div>
    </div>
  )
})

export default MaskTransition
